// M-015 fix: Don't import mock data for production initial state
import type { Chat, ChatMessage } from '@/pages/chat/types';
import { useAuthStore } from '@/stores/auth-store';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** M-030 fix: Maximum chat message length to prevent memory pressure */
const MAX_MESSAGE_LENGTH = 4000;

interface ChatState {
  // Widget állapot
  isOpen: boolean;
  isMinimized: boolean;

  // Aktív chat
  selectedChatId: string | null;

  // Adatok
  chats: Chat[];
  messages: Record<string, ChatMessage[]>;

  // Actions
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  minimizeChat: () => void;
  maximizeChat: () => void;
  selectChat: (chatId: string | null) => void;
  sendMessage: (content: string) => void;
  getTotalUnreadCount: () => number;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      isMinimized: true,
      selectedChatId: null,
      // M-015 fix: Initialize with empty data. Real chat data should be fetched from API on mount.
      chats: [],
      messages: {},

      openChat: () => set({ isOpen: true, isMinimized: false }),

      closeChat: () => set({ isOpen: false, isMinimized: true, selectedChatId: null }),

      toggleChat: () => {
        const state = get();
        if (state.isMinimized) {
          set({ isOpen: true, isMinimized: false });
        } else {
          set({ isMinimized: true });
        }
      },

      minimizeChat: () => set({ isMinimized: true }),

      maximizeChat: () => set({ isOpen: true, isMinimized: false }),

      selectChat: (chatId: string | null) => {
        if (chatId) {
          // Olvasatlan üzenetek nullázása a kiválasztott chat-nél
          set(state => ({
            selectedChatId: chatId,
            chats: state.chats.map(chat =>
              chat.id === chatId ? { ...chat, unreadCount: 0 } : chat
            ),
          }));
        } else {
          set({ selectedChatId: null });
        }
      },

      sendMessage: (content: string) => {
        const state = get();
        if (!state.selectedChatId || !content.trim()) return;

        // M-030 fix: Enforce message length limit
        const trimmed = content.trim().slice(0, MAX_MESSAGE_LENGTH);

        // M-015 fix: Get current user from auth store instead of mock data
        const authUser = useAuthStore.getState().user;
        const senderId = authUser?.id ?? 'unknown';
        const senderName = authUser?.name ?? 'Ismeretlen';

        const newMessage: ChatMessage = {
          id: `msg-new-${Date.now()}`,
          chatId: state.selectedChatId,
          senderId,
          senderName,
          content: trimmed,
          timestamp: new Date().toISOString(),
          status: 'sent',
          isOwn: true,
        };

        set(state => ({
          messages: {
            ...state.messages,
            [state.selectedChatId!]: [...(state.messages[state.selectedChatId!] ?? []), newMessage],
          },
          chats: state.chats.map(chat =>
            chat.id === state.selectedChatId
              ? { ...chat, lastMessage: newMessage, updatedAt: newMessage.timestamp }
              : chat
          ),
        }));
      },

      getTotalUnreadCount: () => {
        return get().chats.reduce((sum, chat) => sum + chat.unreadCount, 0);
      },
    }),
    {
      name: 'kgc-chat',
      partialize: state => ({
        isOpen: state.isOpen,
        isMinimized: state.isMinimized,
        selectedChatId: state.selectedChatId,
      }),
    }
  )
);
